import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import ResponsiveNavigation from '../../components/ResponsiveNavigation';

const AdminCompanies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState({ name: '', city: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async (searchFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchFilters.name) params.append('name', searchFilters.name);
      if (searchFilters.city) params.append('city', searchFilters.city);

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/companies?${params.toString()}`
      );
      
      if (response.data.success) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchCompanies();
  };

  const handleReset = () => {
    setFilters({ name: '', city: '' });
    fetchCompanies({ name: '', city: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-companies-page">
      <ResponsiveNavigation />
      
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Companies</h1>
            <button
              onClick={() => navigate('/admin/companies/add')}
              className="text-blue-600 hover:text-blue-700 font-medium"
              data-testid="add-company-btn"
            >
              + add company
            </button>
          </div>

          {/* Search Filters — pill-style search bar (matches recruiter MyCandidates) */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row items-stretch bg-white rounded-full shadow-sm border-2 border-gray-900/90 focus-within:border-blue-500 transition-colors overflow-hidden">
              <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-3.5 border-r border-gray-200">
                <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Company name..."
                  value={filters.name}
                  onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
                  data-testid="filter-company-name"
                />
                {filters.name && (
                  <button type="button" onClick={() => setFilters({ ...filters, name: '' })} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Clear name">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0 px-5 py-3.5">
                <input
                  type="text"
                  placeholder="City..."
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
                  data-testid="filter-city"
                />
                {filters.city && (
                  <button type="button" onClick={() => setFilters({ ...filters, city: '' })} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Clear city">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <button
                onClick={handleReset}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm px-8 py-3.5 sm:m-2 rounded-full transition-colors"
                data-testid="reset-filter-btn"
              >
                Reset filters
              </button>
            </div>
          </div>

          {/* Companies Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Open positions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Number of licenses
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-testid="companies-table-body">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : companies.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      No companies found
                    </td>
                  </tr>
                ) : (
                  companies.map((company) => (
                    <tr
                      key={company.id}
                      onClick={() => navigate(`/admin/companies/${company.id}`)}
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                      data-testid={`company-row-${company.id}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.city}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.numberOfVacancies}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.numberOfLicenses}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCompanies;
